import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';

export interface EmailCampaignPreview {
  html: string;
  subject: string;
}

class PreviewReadFailed extends Error {
  constructor(readonly status: number) {
    super(`email-campaign-preview ${status}`);
  }
}

export async function fetchEmailCampaignPreview(workspaceId: string, metaSiteId: string, campaignId: string): Promise<EmailCampaignPreview> {
  const query = new URLSearchParams({ workspaceId, metaSiteId, campaignId });
  const res = await apiFetch(`${API_BASE}/api/email-campaign-preview?${query.toString()}`, { credentials: 'include' });
  if (!res.ok) throw new PreviewReadFailed(res.status);
  const body = (await res.json().catch(() => null)) as Partial<EmailCampaignPreview> | null;
  if (!body?.html) throw new PreviewReadFailed(res.status);
  return { html: body.html, subject: body.subject ?? '' };
}

type EmailCampaignIds = { workspaceId: string; metaSiteId: string; campaignId: string };

const retryPreviewRead = (count: number, error: unknown) =>
  count < 1 && !(error instanceof PreviewReadFailed && error.status >= 400 && error.status < 500);

export function useEmailCampaignPreview(ids: EmailCampaignIds | null) {
  return useQuery({
    queryKey: queryKeys.emailCampaignPreview(ids?.workspaceId ?? '', ids?.metaSiteId ?? '', ids?.campaignId ?? ''),
    queryFn: () => fetchEmailCampaignPreview(ids?.workspaceId ?? '', ids?.metaSiteId ?? '', ids?.campaignId ?? ''),
    enabled: Boolean(ids),
    staleTime: 0,
    retry: retryPreviewRead,
  });
}

export function useEmailCampaignInlinePreview(ids: EmailCampaignIds, cardKey: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.emailCampaignInlinePreview(ids.workspaceId, ids.metaSiteId, ids.campaignId, cardKey),
    queryFn: () => fetchEmailCampaignPreview(ids.workspaceId, ids.metaSiteId, ids.campaignId),
    enabled,
    staleTime: Infinity,
    cacheTime: 30 * 60_000,
    retry: retryPreviewRead,
  });
}
